/**
 * Player Component
 *
 * Multi-provider music player bar.
 * Supports Spotify, Apple Music, YouTube Music, and preview fallback.
 */

import { useState } from 'preact/hooks';
import {
  currentTrack,
  currentProvider,
  isPlaying,
  progress,
  position,
  duration,
  volume,
  togglePlay,
  seek,
  next,
  previous,
  setVolume,
} from '@/stores/player';
import { currentSong, vote, userVote } from '@/stores/songs';
import type { MusicProvider } from '@/types';

// Provider-specific URL generators
const PROVIDER_URLS = {
  spotify: {
    track: (id: string) => `https://open.spotify.com/track/${id}`,
    artist: (id: string) => `https://open.spotify.com/artist/${id}`,
    album: (id: string) => `https://open.spotify.com/album/${id}`,
    logo: '/images/Full_Logo_Green_RGB.svg',
    name: 'Spotify',
    color: '#1DB954',
  },
  apple_music: {
    track: (id: string) => `https://music.apple.com/song/${id}`,
    artist: (id: string) => `https://music.apple.com/artist/${id}`,
    album: (id: string) => `https://music.apple.com/album/${id}`,
    logo: '/images/providers/apple_music.svg',
    name: 'Apple Music',
    color: '#FC3C44',
  },
  youtube_music: {
    track: (id: string) => `https://music.youtube.com/watch?v=${id}`,
    artist: (id: string) => `https://music.youtube.com/channel/${id}`,
    album: (id: string) => `https://music.youtube.com/playlist?list=${id}`,
    logo: '/images/providers/youtube_music.svg',
    name: 'YouTube Music',
    color: '#FF0000',
  },
  amazon_music: {
    track: (id: string) => `https://music.amazon.com/tracks/${id}`,
    artist: (id: string) => `https://music.amazon.com/artists/${id}`,
    album: (id: string) => `https://music.amazon.com/albums/${id}`,
    logo: '/images/providers/amazon_music.svg',
    name: 'Amazon Music',
    color: '#00A8E1',
  },
};

export function Player() {
  const [isDragging, setIsDragging] = useState(false);

  // Get track info from player store or current song
  const track = currentTrack.value;
  const provider = currentProvider.value;
  const song = currentSong.value;
  const currentVoteValue = userVote.value;

  // Determine which provider config to use
  const providerConfig = provider && provider !== 'preview'
    ? PROVIDER_URLS[provider as MusicProvider]
    : PROVIDER_URLS.spotify; // Default to Spotify styling

  // Extract track info (handles multiple track formats)
  const title = track
    ? ('name' in track ? track.name : 'title' in track ? track.title : 'Not Playing')
    : 'Not Playing';

  const artist = track
    ? ('artists' in track
        ? (track.artists as Array<{ name: string }>).map(a => a.name).join(', ')
        : 'artist' in track
        ? track.artist
        : 'artistName' in track
        ? (track as { artistName: string }).artistName
        : 'Select a song to play')
    : 'Select a song to play';

  const album = track
    ? ('album' in track && typeof track.album === 'object'
        ? (track.album as { name?: string })?.name || ''
        : 'album' in track && typeof track.album === 'string'
        ? track.album
        : 'albumName' in track
        ? (track as { albumName: string }).albumName
        : '')
    : '';

  // Get album art (handles Spotify, Apple Music, and our Song types)
  const albumArt = track
    ? ('album' in track && typeof track.album === 'object'
        ? ((track.album as { images?: Array<{ url: string }> })?.images?.[0]?.url || '/images/logo-transparent_nobuffer_crop_square.png')
        : 'albumArt' in track
        ? track.albumArt || '/images/logo-transparent_nobuffer_crop_square.png'
        : 'image' in track
        ? track.image || '/images/logo-transparent_nobuffer_crop_square.png'
        : 'attributes' in track && (track as { attributes?: { artwork?: { url: string } } }).attributes?.artwork
        ? (track as { attributes: { artwork: { url: string } } }).attributes.artwork.url.replace('{w}', '300').replace('{h}', '300')
        : '/images/logo-transparent_nobuffer_crop_square.png')
    : '/images/logo-transparent_nobuffer_crop_square.png';

  // Get track/artist/album IDs for links
  const trackId = track
    ? ('id' in track ? (track as { id: string }).id
      : 'spotifyId' in track ? (track as { spotifyId: string }).spotifyId
      : 'trackId' in track ? (track as { trackId: string }).trackId
      : '')
    : '';

  const artistId = track && 'artists' in track && Array.isArray(track.artists) && track.artists[0]
    ? (track.artists[0] as { id?: string }).id || ''
    : track && 'artistId' in track
    ? (track as { artistId: string }).artistId
    : '';

  const albumId = track && 'album' in track && typeof track.album === 'object'
    ? (track.album as { id?: string })?.id || ''
    : track && 'albumId' in track
    ? (track as { albumId: string }).albumId
    : '';

  const handleProgressClick = (e: MouseEvent) => {
    const bar = e.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newPosition = percent * duration.value;
    seek(newPosition);
  };

  const handleVolumeClick = (e: MouseEvent) => {
    const bar = e.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setVolume(percent);
  };

  const handleVote = async (voteType: 'like' | 'neutral' | 'dislike') => {
    if (!song) return;
    await vote(song.id, voteType);
  };

  return (
    <div class="spotify-player">
      {/* Album Art */}
      <img
        src={albumArt}
        alt="Album art"
        class="player-album-art"
        id="player-album-art"
      />

      {/* Player Info Section */}
      <div class="player-info">
        <a
          class="spotify-content-link"
          id="player-track-link"
          href={trackId ? providerConfig.track(trackId) : '#'}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div class="player-title" id="player-title">{title}</div>
        </a>
        <a
          class={`spotify-content-link ${track ? '' : 'hidden'}`}
          id="player-artist-link"
          href={artistId ? providerConfig.artist(artistId) : '#'}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div class="player-artist" id="player-artist">{track ? artist : ''}</div>
        </a>
        <div class={`player-artist ${track ? 'hidden' : ''}`} id="player-artist-fallback">
          {!track ? artist : ''}
        </div>
        <a
          class={`spotify-content-link ${album ? '' : 'hidden'}`}
          id="player-album-link"
          href={albumId ? providerConfig.album(albumId) : '#'}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div class="player-album" id="player-album">{album}</div>
        </a>
        <div class={`player-album ${album ? 'hidden' : ''}`} id="player-album-fallback"></div>
      </div>

      {/* Voting Buttons */}
      <div class="player-voting">
        <button
          class={`player-vote-btn ${currentVoteValue === 'like' ? 'active like' : ''}`}
          id="player-like-btn"
          data-vote="like"
          title="Vote: Like this song"
          onClick={() => handleVote('like')}
          disabled={!song}
        >
          <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.864.046C7.908-.193 7.02.53 6.956 1.466c-.072 1.051-.23 2.016-.428 2.59-.125.36-.479 1.013-1.04 1.639-.557.623-1.282 1.178-2.131 1.41C2.685 7.288 2 7.87 2 8.72v4.001c0 .845.682 1.464 1.448 1.545 1.07.114 1.564.415 2.068.723l.048.03c.272.165.578.348.97.484.397.136.861.217 1.466.217h3.5c.937 0 1.599-.477 1.934-1.064a1.86 1.86 0 0 0 .254-.912c0-.152-.023-.312-.077-.464.201-.263.38-.578.488-.901.11-.33.172-.762.004-1.149.069-.13.12-.269.159-.403.077-.27.113-.568.113-.857 0-.288-.036-.585-.113-.856a2.144 2.144 0 0 0-.138-.362 1.9 1.9 0 0 0 .234-1.734c-.206-.592-.682-1.1-1.2-1.272-.847-.282-1.803-.276-2.516-.211a9.84 9.84 0 0 0-.443.05 9.365 9.365 0 0 0-.062-4.509A1.38 1.38 0 0 0 9.125.111L8.864.046zM11.5 14.721H8c-.51 0-.863-.069-1.14-.164-.281-.097-.506-.228-.776-.393l-.04-.024c-.555-.339-1.198-.731-2.49-.868-.333-.036-.554-.29-.554-.55V8.72c0-.254.226-.543.62-.65 1.095-.3 1.977-.996 2.614-1.708.635-.71 1.064-1.475 1.238-1.978.243-.7.407-1.768.482-2.85.025-.362.36-.594.667-.518l.262.066c.16.04.258.143.288.255a8.34 8.34 0 0 1-.145 4.725.5.5 0 0 0 .595.644l.003-.001.014-.003.058-.014a8.908 8.908 0 0 1 1.036-.157c.663-.06 1.457-.054 2.11.164.175.058.45.3.57.65.107.308.087.67-.266 1.022l-.353.353.353.354c.043.043.105.141.154.315.048.167.075.37.075.581 0 .212-.027.414-.075.582-.05.174-.111.272-.154.315l-.353.353.353.354c.047.047.109.177.005.488a2.224 2.224 0 0 1-.505.805l-.353.353.353.354c.006.005.041.05.041.17a.866.866 0 0 1-.121.416c-.165.288-.503.56-1.066.56z"/>
          </svg>
        </button>
        <button
          class={`player-vote-btn ${currentVoteValue === 'neutral' ? 'active neutral' : ''}`}
          id="player-neutral-btn"
          data-vote="neutral"
          title="Vote: Neutral on this song"
          onClick={() => handleVote('neutral')}
          disabled={!song}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <button
          class={`player-vote-btn ${currentVoteValue === 'dislike' ? 'active dislike' : ''}`}
          id="player-dislike-btn"
          data-vote="dislike"
          title="Vote: Dislike this song"
          onClick={() => handleVote('dislike')}
          disabled={!song}
        >
          <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.864 15.674c-.956.24-1.843-.484-1.908-1.42-.072-1.05-.23-2.015-.428-2.59-.125-.36-.479-1.012-1.04-1.638-.557-.624-1.282-1.179-2.131-1.41C2.685 8.432 2 7.85 2 7V3c0-.845.682-1.464 1.448-1.546 1.07-.113 1.564-.415 2.068-.723l.048-.029c.272-.166.578-.349.97-.484C6.931.08 7.395 0 8 0h3.5c.937 0 1.599.478 1.934 1.064.164.287.254.607.254.913 0 .152-.023.312-.077.464.201.262.38.577.488.9.11.33.172.762.004 1.15.069.13.12.268.159.403.077.27.113.567.113.856s-.036.586-.113.856c-.035.12-.08.244-.138.363.394.571.418 1.2.234 1.733-.206.592-.682 1.1-1.2 1.272-.847.283-1.803.276-2.516.211a10 10 0 0 1-.443-.05 9.36 9.36 0 0 1-.062 4.51c-.138.508-.55.848-1.012.964zM11.5 1H8c-.51 0-.863.068-1.14.163-.281.097-.506.229-.776.393l-.04.025c-.555.338-1.198.73-2.49.868-.333.035-.554.29-.554.55V7c0 .255.226.543.62.65 1.095.3 1.977.997 2.614 1.709.635.71 1.064 1.475 1.238 1.977.243.7.407 1.768.482 2.85.025.362.36.595.667.518l.262-.065c.16-.040.258-.144.288-.255a8.34 8.34 0 0 0-.145-4.726.5.5 0 0 1 .595-.643h.003l.014.004.058.013a8.912 8.912 0 0 0 1.036.157c.663.06 1.457.054 2.11-.163.175-.059.45-.301.57-.651.107-.308.087-.67-.266-1.021L12.793 7l.353-.354c.043-.042.105-.14.154-.315.048-.167.075-.37.075-.581 0-.211-.027-.414-.075-.581-.05-.174-.111-.273-.154-.315l-.353-.354.353-.354c.047-.047.109-.176.005-.488a2.224 2.224 0 0 0-.505-.804l-.353-.354.353-.354c.006-.005.041-.05.041-.17a.866.866 0 0 0-.121-.415C12.4 1.272 12.063 1 11.5 1z"/>
          </svg>
        </button>
      </div>

      {/* Playback Controls */}
      <div class="player-controls">
        <button class="player-control-btn" id="prev-btn" onClick={previous} title="Previous track">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M7 6V18M17 6L9 12L17 18V6Z" stroke="white" stroke-width="2" />
          </svg>
        </button>

        <button class="player-control-btn play-btn" id="play-btn" onClick={togglePlay} title={isPlaying.value ? 'Pause' : 'Play'}>
          {isPlaying.value ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="6" y="5" width="4" height="14" fill="white" />
              <rect x="14" y="5" width="4" height="14" fill="white" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M8 5V19L19 12L8 5Z" fill="white" />
            </svg>
          )}
        </button>

        <button class="player-control-btn" id="next-btn" onClick={next} title="Next track">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M17 6V18M7 6L15 12L7 18V6Z" stroke="white" stroke-width="2" />
          </svg>
        </button>
      </div>

      {/* Progress Bar */}
      <div class="player-progress">
        <div class="progress-spacer"></div>
        <div
          class={`progress-bar ${isDragging ? 'dragging' : ''}`}
          id="progress-bar"
          onClick={handleProgressClick}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
        >
          <div class="progress-fill" id="progress-fill" style={{ width: `${progress.value}%` }}></div>
          <div class="progress-handle" id="progress-handle" style={{ left: `${progress.value}%` }}></div>
        </div>
        <div class="progress-time">
          <span id="current-time">{formatTime(position.value)}</span>
          <span id="total-time">{formatTime(duration.value)}</span>
        </div>
      </div>

      {/* Volume Control */}
      <div class="player-volume">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M8.5 3.5L4 7H2V13H4L8.5 16.5V3.5Z" fill="white" />
          {volume.value > 0.5 && (
            <path d="M14.5 6.5C15.8807 7.8807 15.8807 12.1193 14.5 13.5" stroke="white" stroke-width="2" />
          )}
          {volume.value > 0 && (
            <path d="M11.5 8.5C12.0477 9.0477 12.0477 10.9523 11.5 11.5" stroke="white" stroke-width="2" />
          )}
        </svg>
        <div class="volume-bar" id="volume-bar" onClick={handleVolumeClick}>
          <div class="volume-fill" id="volume-fill" style={{ width: `${volume.value * 100}%` }}></div>
        </div>
      </div>

      {/* Provider Attribution - changes based on active provider */}
      <a
        href={provider === 'apple_music' ? 'https://music.apple.com' :
              provider === 'youtube_music' ? 'https://music.youtube.com' :
              provider === 'amazon_music' ? 'https://music.amazon.com' :
              'https://spotify.com'}
        target="_blank"
        rel="noopener noreferrer"
        class="spotify-attribution"
        title={`Powered by ${providerConfig.name}`}
      >
        <img src={providerConfig.logo} alt={providerConfig.name} />
      </a>
    </div>
  );
}

function formatTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}
